import { useQuery } from '@tanstack/react-query';

import { Schedule, HolidayResponse } from '../types';

import { getSchedule } from './get-schedule';
import { getStudentHolidays } from './get-student-holidays';

// Query Keys for Calendar
const calendarKeys = {
  all: ['calendar'] as const,
  schedule: () => [...calendarKeys.all, 'schedule'] as const,
  holidays: (eventStatus: string, userId: string) =>
    [...calendarKeys.all, 'holidays', eventStatus, userId] as const,
} as const;

// Get Schedule Query
const useSchedule = () => {
  return useQuery<Schedule.Response>({
    queryKey: calendarKeys.schedule(),
    queryFn: getSchedule,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

// Get Student Holidays Query
const useStudentHolidays = (
  eventStatus: string,
  userId: string,
  enabled = true
) => {
  return useQuery<HolidayResponse>({
    queryKey: calendarKeys.holidays(eventStatus, userId),
    queryFn: () => getStudentHolidays(eventStatus, userId),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (holidays don't change often)
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    refetchOnWindowFocus: false,
    enabled: enabled && !!eventStatus && !!userId,
  });
};

// Schedule Hook
export const useScheduleData = () => {
  const { data, isLoading, isError, error, refetch } = useSchedule();
  // This needs to be refactored once API is updated to return only lowercase
  const lessons =
    data?.lessons?.map(l => {
      if (l.status) {
        l.status = String(l.status).toLowerCase() as Schedule.Lesson['status'];
      }
      if (l.variant) {
        l.variant = String(
          l.variant
        ).toLowerCase() as Schedule.Lesson['variant'];
      }
      // Transform joining_link from API to joiningLink for component
      if ('joining_link' in l && l.joining_link) {
        l.joiningLink = l.joining_link;
      }
      return l;
    }) ?? [];

  // Helper to separate past and upcoming lessons
  const pastLessons = lessons.filter(
    lesson => lesson.status === 'attended' || lesson.status === 'missed'
  );

  const upcomingLessons = lessons.filter(
    lesson => lesson.status === 'upcoming' || lesson.status === 'holiday'
  );

  return {
    // Data
    lessons,
    pastLessons,
    upcomingLessons,
    totalLessons: lessons.length,
    timezone: data?.timezone,

    // API response meta
    status: data?.status,
    message: data?.message,

    // States
    isLoading,
    isError,
    error,

    // Actions
    refetch,
  };
};

// Student Holidays Hook
export const useStudentHolidaysData = (
  eventStatus = 'active',
  userId: string,
  enabled = true
) => {
  const holidaysQuery = useStudentHolidays(eventStatus, userId, enabled);

  return {
    // Data
    holidays: holidaysQuery.data?.holidays ?? [],

    // States
    isLoading: holidaysQuery.isLoading,
    isError: holidaysQuery.isError,
    error: holidaysQuery.error,
    isSuccess: holidaysQuery.isSuccess,

    // Actions
    refetch: holidaysQuery.refetch,
  };
};

// Export query keys for external use
export { calendarKeys };
